// pages/W_HR_R330.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import DataGrid, {
  Column,
  Paging,
  FilterRow,
  HeaderFilter,
  SearchPanel,
  Sorting,
  Export,
  Summary,
  TotalItem,
} from "devextreme-react/data-grid";
import DateBox from "devextreme-react/date-box";
import SelectBox from "devextreme-react/select-box";
import { Button } from 'devextreme-react/button';
import 'devextreme/dist/css/dx.material.blue.light.css';
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import { exportDataGrid } from 'devextreme/excel_exporter';
import API_URL from '../config';

export default function W_HR_R330({ user }) {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [custList, setCustList] = useState([]);
  const [selectedCust, setSelectedCust] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [comboLoading, setComboLoading] = useState(false);
  const [error, setError] = useState("");
  
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth);
  const [dateTo, setDateTo] = useState(today);

  const formatDate = (date) => {
    if (!date) return '';
    const y = date.getFullYear().toString();
    const m = (date.getMonth() + 1).toString().padStart(2, "0");
    const d = date.getDate().toString().padStart(2, "0");
    return `${y}${m}${d}`;
  };

  useEffect(() => {
    (async () => {
      setComboLoading(true);
      setError("");
      try {
        const res = await axios.get(`${API_URL}/api/oracle/cust`);
        if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
        setCustList(res.data.data || []);
      } catch (e) {
        setError(`CUST 로드 오류: ${e.message}`);
      } finally {
        setComboLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (custList.length > 0 && user?.role === "CUST") {
      const me = (custList || []).find(i =>
        i.CODE_CD.trim().toUpperCase() === user.username.trim().toUpperCase()
      );
      if (me) {
        setSelectedCust(me.CODE_CD);
      } else {
        setError(`사용자 코드(${user.username})가 목록에 없습니다.`);
      }
    }
  }, [custList, user]);

  const fetchData = async () => {
    if (!selectedCust) {
      setError("Please select CUST.");
      return;
    }
    setLoading(true);
    setError("");
    setData([]);
    try {
      const params = {
        inCustCd: selectedCust,
        inDateFrom: formatDate(dateFrom),
        inDateTo: formatDate(dateTo),
      };
      const res = await axios.get(`${API_URL}/api/oracle/W_HR_R330`, { params });

      if (res.data.retCode !== 0) throw new Error(res.data.retMsg);
      
      const mappedData = (res.data.data || []).map((row, idx) => ({
        ...row,
        id: `${row.CUST_CD}-${row.DEVICE || ''}-${row.LOT_NO || ''}-${idx}`,
      }));
      setData(mappedData);
    } catch (e) {
      const errorMessage = e.response?.data?.retMsg || e.message || "알 수 없는 오류가 발생했습니다.";
      setError(`조회 오류: ${errorMessage}`);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const onExporting = (e) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("MONTHLY INOUT LIST");
    exportDataGrid({
        component: e.component,
        worksheet: worksheet,
        autoFilterEnabled: true,
    }).then(() => {
        workbook.xlsx.writeBuffer().then(buffer => {
        saveAs(new Blob([buffer], { type: "application/octet-stream" }), "MONTHLY_INOUT_LIST.xlsx");
        });
    });
    e.cancel = true;
  };

  return (
    // [수정] 페이지 전체를 Flexbox 레이아웃으로 설정
    <div style={{ padding: 0, fontFamily: 'Arial, sans-serif', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* 1. 검색 영역 */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '20px' }}>MONTHLY INOUT LIST</h3>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: 'center' }}>
          <DateBox value={dateFrom} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateFrom(e.value)} label="FROM" />
          <DateBox value={dateTo} displayFormat="yyyy-MM-dd" type="date" onValueChanged={e => setDateTo(e.value)} label="TO" />
          <SelectBox
            dataSource={custList}
            value={selectedCust}
            valueExpr="CODE_CD"
            displayExpr="CODE_CD"
            onValueChanged={e => setSelectedCust(e.value)}
            disabled={comboLoading || user?.role === "CUST"}
            searchEnabled
            showClearButton={user?.role !== "CUST"}
            placeholder="Select CUST"
            label="CUST"
          />
          <Button text="Search" type="default" icon="search" onClick={fetchData} disabled={loading} />
        </div>
        {error && <p style={{ color: "#d9534f", fontWeight: 'bold' }}>{error}</p>}
      </div>
      
      {/* 2. 그리드 영역 (남은 공간을 모두 차지) */}
      <div style={{ flex: 1, position: 'relative' }}> {/* [수정] position: 'relative' 추가 */}
        {/* 로딩 인디케이터를 그리드 영역 안으로 이동 */}
        {loading && (
          <div className="loading-overlay">
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading...</div>
          </div>
        )}
        <DataGrid
            dataSource={data}
            keyExpr="id"
            onExporting={onExporting}
            height="100%" // [수정] 부모의 높이를 100% 사용
            showBorders={true}
            columnAutoWidth={true}
            allowColumnResizing={true}
            hoverStateEnabled={true}
        >
            <Export enabled={true} allowExportSelectedData={false} />
            <Paging defaultPageSize={20} />
            <FilterRow visible={true} />
            <HeaderFilter visible={true} />   
            <SearchPanel visible={true} width={240} highlightCaseSensitive={false} />
            <Sorting mode="multiple" />

            <Column dataField="CUST_CD" caption="CUST" />
            <Column dataField="DEVICE" caption="DEVICE" />
            <Column dataField="LOT_NO" caption="LOT NO" />
            <Column dataField="SUB" caption="SUB" />
            <Column dataField="BOH" caption="BOH" dataType="number" alignment="right" />
            <Column dataField="IN_QTY" caption="IN QTY" dataType="number" alignment="right" />
            <Column dataField="SHIP_QTY" caption="SHIP QTY" dataType="number" alignment="right" />
            <Column dataField="REJECT" caption="REJECT" dataType="number" alignment="right" />
            <Column dataField="LIS_REJECT" caption="LIS REJECT" dataType="number" alignment="right" />
            <Column dataField="TEST_TEJECT" caption="TEST REJECT" dataType="number" alignment="right" />
            <Column dataField="TRANS" caption="TRANS" dataType="number" alignment="right" />
            <Column dataField="CV" caption="CV" dataType="number" alignment="right" />
            <Column dataField="EOH" caption="EOH" dataType="number" alignment="right" fixed={true} fixedPosition="right" />
            
            <Summary>
                <TotalItem column="LOT_NO" summaryType="count" displayFormat="총 {0} 건" />
                <TotalItem column="BOH" summaryType="sum" valueFormat="#,##0" />
                <TotalItem column="IN_QTY" summaryType="sum" valueFormat="#,##0" />
                <TotalItem column="SHIP_QTY" summaryType="sum" valueFormat="#,##0" />
                <TotalItem column="EOH" summaryType="sum" valueFormat="#,##0" />
            </Summary>
        </DataGrid>
      </div>
    </div>
  );
}